// pages/admin/FirmManagement.jsx — B2: Firm Management, AFS Jobs, Support Log
import { useState, useEffect, useCallback } from 'react';
import {
  adminListFirms, adminGetFirm, adminResetUsage,
  adminChangeSeats, adminSetFirmStatus, adminSendFirmMessage, adminVerifyEmail,
  adminSendVerificationLink, adminDeleteFirm,
  adminListJobs, addSupportNote,
} from '../api/kailux';
import EmptyState from '../components/EmptyState';
import Spinner from '../components/Spinner'

import { ICONS } from '../components/Icon'

import { T, alpha } from '../theme/theme'
const inp = {
  padding:'8px 12px', background:T.s1, border:`1px solid ${T.bd}`,
  borderRadius:'8px', color:T.tx, fontSize:'14px', outline:'none',
  fontFamily:"'Outfit',sans-serif",
}
const lbl = { fontSize:'10px', fontWeight:700, color:T.lt, textTransform:'uppercase', letterSpacing:'0.08em' }

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-ZA', { day:'2-digit', month:'short', year:'numeric' });
}

// Date AND time, always in South African time so the admin board reads the same
// wherever the admin's browser is set (Oct 2026: last login used to show date only).
function fmtDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-ZA', {
    day:'2-digit', month:'short', year:'numeric',
    hour:'2-digit', minute:'2-digit', hour12:false, timeZone:'Africa/Johannesburg',
  }) + ' SAST';
}

function StatusBadge({ status }) {
  const map = {
    active:    { bg:T.gnd,  color:T.gn },
    unpaid:    { bg:T.bld,  color:T.bl },
    suspended: { bg:T.rdd,  color:T.rd },
    completed: { bg:T.gnd,  color:T.gn },
    pending:   { bg:T.s3,   color:T.lt },
  };
  const c = map[status] || { bg:T.s3, color:T.lt };
  return (
    <span style={{ padding:'3px 9px', borderRadius:'999px', fontSize:'12px', fontWeight:600,
      background:c.bg, color:c.color, textTransform:'capitalize' }}>
      {status}
    </span>
  );
}

function FirmDrawer({ firmId, onClose, onDeleted }) {
  const [data,      setData]      = useState(null);
  const [activeTab, setTab]       = useState('overview');
  const [note,      setNote]      = useState('');
  const [loading,   setLoading]   = useState(true);
  const [toast,     setToast]     = useState(null);
  const [msgSubject,setMsgSubject]= useState('');
  const [msgBody,   setMsgBody]   = useState('');
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [deleting,  setDeleting]  = useState(false);

  function showToast(msg, type='success') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }

  const load = useCallback(() => {
    setLoading(true);
    adminGetFirm(firmId)
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [firmId]);

  useEffect(() => { load(); }, [load]);

  async function handleResetUsage() {
    if (!window.confirm(
      `Reset ${firm.name || 'this firm'}'s AFS count from ${firm.afs_count ?? 0} back to 0?\n\n` +
      `This cannot be undone automatically — if the firm has genuinely used ` +
      `some of their monthly AFS credits, resetting will give them extra ` +
      `credits for this billing period.`
    )) return;
    await adminResetUsage(firmId, 0);
    showToast('Usage reset to 0');
    load();
  }
  async function handleChangeSeats(userCount) {
    if (!window.confirm(
      `Change ${firm.name || 'this firm'}'s seat count from ${firm.user_count ?? 1} to ${userCount}?\n\n` +
      `This also recalculates their monthly AFS credit pool automatically.`
    )) return false;
    await adminChangeSeats(firmId, userCount);
    showToast(`Seats → ${userCount}`);
    load();
    return true;
  }
  async function handleVerifyEmail() {
    const res = await adminVerifyEmail(firmId);
    showToast(res.already_verified ? 'Already verified' : 'Email manually verified');
    load();
  }
  async function handleSendVerificationLink() {
    const res = await adminSendVerificationLink(firmId);
    showToast(res.already_verified ? 'Already verified'
      : res.email_sent ? 'Verification link sent' : 'Could not send email — check Resend logs');
    load();
  }
  async function handleToggleStatus() {
    const cur = data.firm.subscription_status;
    const next = cur === 'suspended' ? 'active' : 'suspended';
    const confirmMsg = next === 'suspended'
      ? `Suspend ${firm.name || 'this firm'}? This immediately cuts off their platform access.`
      : `Unsuspend ${firm.name || 'this firm'} and restore their platform access?`;
    if (!window.confirm(confirmMsg)) return;
    await adminSetFirmStatus(firmId, next);
    showToast(`Firm ${next}`);
    load();
  }
  async function handleSendMessage() {
    await adminSendFirmMessage(firmId, msgSubject, msgBody);
    showToast('Message sent');
    setMsgSubject(''); setMsgBody('');
  }
  async function handleAddNote() {
    if (!note.trim()) return;
    await addSupportNote(firmId, note);
    setNote(''); showToast('Note added'); load();
  }
  async function handleDeleteFirm() {
    setDeleting(true);
    try {
      await adminDeleteFirm(firmId, deleteConfirmName);
      onDeleted?.();
    } catch (e) {
      showToast(e?.response?.data?.detail || 'Delete failed', 'error');
      setDeleting(false);
    }
  }

  if (loading) return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:50,
      display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:T.s2, border:`1px solid ${T.bd}`, borderRadius:'12px', padding:'28px 36px',
        textAlign:'center' }}><Spinner fontSize='14px'/></div>
    </div>
  );

  if (!data) return null;
  const { firm, clients, jobs, support } = data;

  const drawerTabs = ['overview','jobs','support','message'];

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:50,
      display:'flex', justifyContent:'flex-end' }}>
      <div style={{ width:'100%', maxWidth:'640px', background:T.s1,
        height:'100%', overflowY:'auto', display:'flex', flexDirection:'column',
        boxShadow:T.shl }}>

        {/* Header */}
        <div style={{ padding:'18px 22px', borderBottom:`1px solid ${T.bd}`,
          display:'flex', alignItems:'flex-start', justifyContent:'space-between',
          position:'sticky', top:0, background:T.s1, zIndex:10 }}>
          <div>
            <h3 style={{ fontSize:'16px', fontWeight:700, color:T.tx, margin:0 }}>{firm.name}</h3>
            <p style={{ fontSize:'13px', color:T.mt, marginTop:'3px' }}>{firm.owner_email}</p>
          </div>
          <button onClick={onClose}
            style={{ background:'none', border:'none', cursor:'pointer', color:T.mt, fontSize:'22px',
              lineHeight:1, padding:'4px 8px' }}>×</button>
        </div>

        {/* Toast */}
        {toast && (
          <div style={{ margin:'14px 22px 0', padding:'10px 14px', borderRadius:'8px', fontSize:'14px', fontWeight:600,
            background:toast.type==='error'?T.rdd:T.gnd, color:toast.type==='error'?T.rd:T.gn }}>
            {toast.msg}
          </div>
        )}

        {/* Tabs */}
        <div style={{ display:'flex', borderBottom:`1px solid ${T.bd}`, padding:'0 22px',
          position:'sticky', top:'61px', background:T.s1, zIndex:10 }}>
          {drawerTabs.map(t => (
            <button key={t} onClick={() => setTab(t)}
              style={{ padding:'10px 14px', fontSize:'13px', fontWeight:600, cursor:'pointer',
                border:'none', background:'transparent', fontFamily:"'Outfit',sans-serif",
                textTransform:'capitalize',
                borderBottom:`2px solid ${activeTab===t?T.or:'transparent'}`,
                marginBottom:'-1px', color:activeTab===t?T.or:T.mt, transition:'all 0.15s' }}>
              {t}
            </button>
          ))}
        </div>

        <div style={{ padding:'20px 22px', flex:1, display:'flex', flexDirection:'column', gap:'20px' }}>

          {/* OVERVIEW */}
          {activeTab === 'overview' && (
            <>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'8px' }}>
                {[
                  { label:'Seats',      value: firm.user_count ?? 1 },
                  { label:'Status',     value: <StatusBadge status={firm.subscription_status} /> },
                  { label:'AFS Used',   value: firm.afs_count ?? 0 },
                  { label:'Clients',    value: firm.client_count ?? 0 },
                  { label:'Total Jobs', value: firm.total_afs_jobs ?? 0 },
                  { label:'Last Login', value: firm.last_login_at
                      ? fmtDateTime(firm.last_login_at)
                      : <span style={{ color:T.mt }}>Never</span> },
                  { label:'Email Verified', value: firm.email_verified_at
                      ? <span style={{ color:T.gn }}>Yes — {fmtDate(firm.email_verified_at)}</span>
                      : <span style={{ color:T.rd }}>No{firm.email_otp_send_count ? ` (${firm.email_otp_send_count} code${firm.email_otp_send_count===1?'':'s'} sent)` : ' (no code ever sent)'}</span> },
                ].map(s => (
                  <div key={s.label} style={{ background:T.s2, border:`1px solid ${T.bd}`,
                    borderRadius:'8px', padding:'12px' }}>
                    <p style={{ fontSize:'10px', color:T.mt, marginBottom:'4px', textTransform:'uppercase',
                      letterSpacing:'0.06em', fontWeight:600 }}>{s.label}</p>
                    <p style={{ fontSize:'14px', fontWeight:700, color:T.tx, margin:0 }}>{s.value}</p>
                  </div>
                ))}
              </div>

              {/* Actions */}
              <div style={{ display:'flex', flexDirection:'column', gap:'12px' }}>
                <p style={lbl}>Actions</p>

                <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                  <span style={{ fontSize:'14px', color:T.mt, width:'130px', flexShrink:0 }}>Change seats</span>
                  <input type="number" min="1" defaultValue={firm.user_count || 1}
                    style={{ ...inp, width:'80px' }}
                    onBlur={e => {
                      const n = parseInt(e.target.value, 10)
                      if (n && n >= 1 && n !== firm.user_count) {
                        handleChangeSeats(n).then(proceeded => {
                          // Cancelled at the confirm() prompt — this field would
                          // otherwise be left showing the edited-but-unsaved number,
                          // so snap it back to the actual current value.
                          if (!proceeded) e.target.value = firm.user_count || 1
                        })
                      }
                    }} />
                  <span style={{ fontSize:'12px', color:T.mt }}>users (updates AFS pool automatically)</span>
                </div>

                <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                  <span style={{ fontSize:'14px', color:T.mt, width:'130px', flexShrink:0 }}>Reset AFS count</span>
                  <button onClick={handleResetUsage}
                    style={{ ...inp, cursor:'pointer', border:`1px solid ${T.bd}`, padding:'7px 14px',
                      background:T.s2, color:T.tx }}>
                    Reset to 0
                  </button>
                </div>

                {!firm.email_verified_at && (
                  <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                    <span style={{ fontSize:'14px', color:T.mt, width:'130px', flexShrink:0 }}>Email verification</span>
                    <button onClick={handleSendVerificationLink}
                      style={{ ...inp, cursor:'pointer', border:`1px solid ${alpha(T.or, 0.38)}`, padding:'7px 14px',
                        background:T.ord, color:T.or }}>
                      Send verification link
                    </button>
                    <button onClick={handleVerifyEmail}
                      style={{ ...inp, cursor:'pointer', border:`1px solid ${alpha(T.gn, 0.38)}`, padding:'7px 14px',
                        background:T.gnd, color:T.gn }}>
                      Manually verify
                    </button>
                    <span style={{ fontSize:'12px', color:T.mt }}>
                      link = self-serve, one click, 24h; manual = immediate override for stuck cases
                    </span>
                  </div>
                )}

                <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                  <span style={{ fontSize:'14px', color:T.mt, width:'130px', flexShrink:0 }}>
                    {firm.subscription_status==='suspended'?'Unsuspend':'Suspend'} firm
                  </span>
                  <button onClick={handleToggleStatus}
                    style={{ ...inp, cursor:'pointer', padding:'7px 14px',
                      border:`1px solid ${firm.subscription_status==='suspended'?alpha(T.gn, 0.38):alpha(T.rd, 0.38)}`,
                      background:firm.subscription_status==='suspended'?T.gnd:T.rdd,
                      color:firm.subscription_status==='suspended'?T.gn:T.rd }}>
                    {firm.subscription_status==='suspended'?'Unsuspend':'Suspend'}
                  </button>
                </div>
              </div>

              {/* Danger Zone — permanent firm deletion (kailux-fixes-log.md
                  storage.objects gap follow-up, 10 Sep 2026). Previously
                  cleaning up a test/dormant firm meant a direct Supabase
                  query against `firms` alone, which never touched storage
                  (uploaded documents, generated PDFs, logos, letterheads)
                  at all — this reuses the same _execute_account_deletion
                  cascade the firm's own self-service deletion already uses,
                  so storage cleanup happens correctly via the Storage API
                  every time, not just when someone remembers to do it by
                  hand. Confirmation is the firm's exact name rather than a
                  fixed phrase, since an admin manages many different firms
                  in one session — a fixed phrase becomes muscle memory and
                  stops protecting against picking the wrong one. */}
              <div style={{ borderTop:`1px solid ${alpha(T.rd, 0.19)}`, paddingTop:'16px', marginTop:'4px' }}>
                <p style={{ ...lbl, color:T.rd, marginBottom:'10px' }}>Danger Zone</p>
                <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
                  <p style={{ fontSize:'12px', color:T.mt, margin:0 }}>
                    Permanently deletes this firm's clients, AFS history, documents, team
                    members, and all other data. This cannot be undone. Type the firm's
                    exact name below to confirm.
                  </p>
                  <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                    <input value={deleteConfirmName} onChange={e=>setDeleteConfirmName(e.target.value)}
                      placeholder={firm.name} style={{ ...inp, flex:1 }} />
                    <button onClick={handleDeleteFirm}
                      disabled={deleting || deleteConfirmName.trim() !== (firm.name || '').trim()}
                      style={{ ...inp, padding:'7px 14px',
                        border:`1px solid ${alpha(T.rd, 0.38)}`, background:T.rdd, color:T.rd,
                        opacity:(deleting || deleteConfirmName.trim() !== (firm.name || '').trim()) ? 0.5 : 1,
                        cursor:(deleting || deleteConfirmName.trim() !== (firm.name || '').trim()) ? 'not-allowed' : 'pointer' }}>
                      {deleting ? 'Deleting…' : 'Delete firm permanently'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Clients */}
              {clients.length > 0 && (
                <div>
                  <p style={{ ...lbl, marginBottom:'8px' }}>Clients ({clients.length})</p>
                  <div style={{ border:`1px solid ${T.bd}`, borderRadius:'8px', overflow:'hidden' }}>
                    {clients.map((c,i) => (
                      <div key={c.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                        padding:'10px 14px', borderBottom:i<clients.length-1?`1px solid ${T.bd}`:'none' }}>
                        <span style={{ fontSize:'14px', color:T.tx }}>{c.name}</span>
                        <span style={{ fontSize:'12px', color:T.mt }}>{c.entity_type}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* JOBS */}
          {activeTab === 'jobs' && (
            <div>
              <p style={{ ...lbl, marginBottom:'10px' }}>AFS Jobs ({jobs.length})</p>
              {jobs.length === 0 ? (
                <EmptyState icon={ICONS.receipt} title="No jobs yet" size="compact" />
              ) : (
                <div style={{ border:`1px solid ${T.bd}`, borderRadius:'8px', overflow:'hidden' }}>
                  {jobs.map((j,i) => (
                    <div key={j.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                      padding:'10px 14px', borderBottom:i<jobs.length-1?`1px solid ${T.bd}`:'none' }}>
                      <div>
                        <p style={{ fontSize:'14px', fontWeight:600, color:T.tx, margin:0 }}>
                          {j.financial_year} — {j.clients?.name || '—'}
                        </p>
                        <p style={{ fontSize:'12px', color:T.mt, marginTop:'2px' }}>{fmtDate(j.created_at)}</p>
                      </div>
                      <StatusBadge status={j.status} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* SUPPORT */}
          {activeTab === 'support' && (
            <div style={{ display:'flex', flexDirection:'column', gap:'12px' }}>
              <p style={lbl}>Support Log — internal only</p>
              <div style={{ display:'flex', gap:'8px' }}>
                <input style={{ ...inp, flex:1 }} placeholder="Add a note…"
                  value={note} onChange={e=>setNote(e.target.value)}
                  onKeyDown={e=>e.key==='Enter'&&handleAddNote()} />
                <button onClick={handleAddNote}
                  style={{ padding:'8px 16px', background:T.prg, color:T.onpri, border:'none',
                    borderRadius:'8px', fontSize:'14px', fontWeight:700, cursor:'pointer',
                    fontFamily:"'Outfit',sans-serif" }}>Add</button>
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
                {support.map(s => (
                  <div key={s.id} style={{ background:T.s2, border:`1px solid ${T.bd}`,
                    borderRadius:'8px', padding:'12px 14px' }}>
                    <p style={{ fontSize:'14px', color:T.tx, margin:0 }}>{s.note}</p>
                    <p style={{ fontSize:'12px', color:T.mt, marginTop:'5px' }}>
                      {s.created_by} · {fmtDate(s.created_at)}
                    </p>
                  </div>
                ))}
                {support.length === 0 && <EmptyState icon={ICONS.folder} title="No notes yet" size="compact" />}
              </div>
            </div>
          )}

          {/* MESSAGE */}
          {activeTab === 'message' && (
            <div style={{ display:'flex', flexDirection:'column', gap:'12px' }}>
              <p style={lbl}>Send email to {firm.owner_email}</p>
              <div>
                <label style={{ ...lbl, display:'block', marginBottom:'5px' }}>Subject</label>
                <input style={{ ...inp, width:'100%', boxSizing:'border-box' }}
                  value={msgSubject} onChange={e=>setMsgSubject(e.target.value)} />
              </div>
              <div>
                <label style={{ ...lbl, display:'block', marginBottom:'5px' }}>Message</label>
                <textarea rows={6} style={{ ...inp, width:'100%', boxSizing:'border-box', resize:'none', lineHeight:'1.5' }}
                  value={msgBody} onChange={e=>setMsgBody(e.target.value)} />
              </div>
              <button onClick={handleSendMessage} disabled={!msgSubject||!msgBody}
                style={{ alignSelf:'flex-start', padding:'9px 20px', background:T.prg, color:T.onpri,
                  border:'none', borderRadius:'8px', fontSize:'14px', fontWeight:700,
                  cursor:(!msgSubject||!msgBody)?'not-allowed':'pointer',
                  opacity:(!msgSubject||!msgBody)?0.4:1, fontFamily:"'Outfit',sans-serif" }}>
                Send via Resend
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function FirmManagement() {
  const [firms,        setFirms]  = useState([]);
  const [loading,      setLoading]= useState(true);
  const [search,       setSearch] = useState('');
  const [statusFilter, setSt]     = useState('');
  const [page,         setPage]   = useState(1);
  const [selectedFirm, setSelected]= useState(null);
  const [activeTab,    setTab]    = useState('firms');
  const [jobs,         setJobs]   = useState([]);
  const [jobsLoading,  setJL]     = useState(false);

  const loadFirms = useCallback(() => {
    setLoading(true);
    adminListFirms({ search:search||undefined, status:statusFilter||undefined, page })
      .then(d => { setFirms(d.firms); setLoading(false); })
      .catch(() => setLoading(false));
  }, [search, statusFilter, page]);

  useEffect(() => { loadFirms(); }, [loadFirms]);

  useEffect(() => {
    if (activeTab !== 'all jobs') return;
    setJL(true);
    adminListJobs({ page:1, limit:100 })
      .then(d => { setJobs(d.jobs); setJL(false); })
      .catch(() => setJL(false));
  }, [activeTab]);

  const thStyle = { textAlign:'left', padding:'10px 14px', fontSize:'10px', fontWeight:700,
    color:T.lt, textTransform:'uppercase', letterSpacing:'0.08em' }
  const tdStyle = { padding:'10px 14px', fontSize:'14px', color:T.tx }
  const inpSel  = { ...inp, cursor:'pointer' }

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'16px' }}>
      <div>
        <h2 style={{ fontSize:'18px', fontWeight:700, color:T.tx, margin:0 }}>Firm Management</h2>
        <p style={{ fontSize:'14px', fontWeight:'400', color:T.mt, marginTop:'3px' }}>Manage all firms on the platform.</p>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', borderBottom:`1px solid ${T.bd}` }}>
        {['firms','all jobs'].map(t => (
          <button key={t} onClick={()=>setTab(t)}
            style={{ padding:'9px 18px', fontSize:'14px', fontWeight:600, cursor:'pointer',
              border:'none', background:'transparent', fontFamily:"'Outfit',sans-serif",
              textTransform:'capitalize',
              borderBottom:`2px solid ${activeTab===t?T.or:'transparent'}`,
              marginBottom:'-1px', color:activeTab===t?T.or:T.mt, transition:'all 0.15s' }}>
            {t}
          </button>
        ))}
      </div>

      {/* FIRMS TAB */}
      {activeTab === 'firms' && (
        <>
          <div style={{ display:'flex', gap:'8px', flexWrap:'wrap' }}>
            <input style={{ ...inp, width:'220px' }} placeholder="Search firm name…"
              value={search} onChange={e=>{setSearch(e.target.value);setPage(1);}} />
            <select style={inpSel} value={statusFilter} onChange={e=>setSt(e.target.value)}>
              <option value="">All statuses</option>
              <option value="unpaid">Unpaid</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
            </select>
            <button onClick={loadFirms}
              style={{ ...inp, cursor:'pointer', background:T.s2, color:T.tx }}>Refresh</button>
          </div>

          {loading ? (
            <div style={{ height:'160px', display:'flex', alignItems:'center', justifyContent:'center', color:T.mt }}>
              Loading…
            </div>
          ) : (
            <div style={{ border:`1px solid ${T.bd}`, borderRadius:'12px', overflow:'hidden' }}>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead style={{ background:T.s2 }}>
                  <tr>
                    {['Firm','Seats','Status','AFS Used','Clients','Last AFS','Last Login','Joined',''].map(h => (
                      <th key={h} style={thStyle}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {firms.map((f,i) => (
                    <tr key={f.id} style={{ borderTop:`1px solid ${T.bd}` }}>
                      <td style={tdStyle}>
                        <p style={{ fontWeight:600, margin:0 }}>{f.name}</p>
                        <p style={{ fontSize:'12px', color:T.mt, marginTop:'2px' }}>{f.owner_email}</p>
                      </td>
                      <td style={{ ...tdStyle, color:T.lt }}>{f.user_count ?? 1}</td>
                      <td style={tdStyle}><StatusBadge status={f.subscription_status} /></td>
                      <td style={{ ...tdStyle, color:T.lt }}>{f.afs_count??0}</td>
                      <td style={{ ...tdStyle, color:T.lt }}>{f.client_count??0}</td>
                      <td style={{ ...tdStyle, color:T.mt }}>{fmtDate(f.last_afs_at)}</td>
                      <td style={{ ...tdStyle,
                        color: (f.subscription_status === 'active' &&
                                (!f.last_login_at || (Date.now() - new Date(f.last_login_at).getTime()) > 90*24*60*60*1000))
                                ? T.rd : T.mt }}
                        title={f.subscription_status === 'active' && (!f.last_login_at ||
                          (Date.now() - new Date(f.last_login_at).getTime()) > 90*24*60*60*1000)
                          ? 'Paying but no activity for 90+ days' : undefined}>
                        {f.last_login_at ? fmtDateTime(f.last_login_at) : 'Never'}
                      </td>
                      <td style={{ ...tdStyle, color:T.mt }}>{fmtDate(f.created_at)}</td>
                      <td style={tdStyle}>
                        <button onClick={()=>setSelected(f.id)}
                          style={{ fontSize:'13px', color:T.or, border:`1px solid ${alpha(T.or, 0.25)}`,
                            borderRadius:'6px', padding:'5px 12px', background:T.ord,
                            cursor:'pointer', fontFamily:"'Outfit',sans-serif", fontWeight:600 }}>
                          View →
                        </button>
                      </td>
                    </tr>
                  ))}
                  {firms.length === 0 && (
                    <tr><td colSpan={8} style={{ textAlign:'center', padding:'40px', color:T.mt, fontSize:'14px' }}>
                      No firms found
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
            fontSize:'14px', color:T.mt }}>
            <span>{firms.length} results</span>
            <div style={{ display:'flex', gap:'8px' }}>
              <button disabled={page===1} onClick={()=>setPage(p=>p-1)}
                style={{ ...inp, cursor:page===1?'not-allowed':'pointer', opacity:page===1?0.4:1 }}>← Prev</button>
              <span style={{ padding:'8px 12px' }}>Page {page}</span>
              <button disabled={firms.length<25} onClick={()=>setPage(p=>p+1)}
                style={{ ...inp, cursor:firms.length<25?'not-allowed':'pointer', opacity:firms.length<25?0.4:1 }}>Next →</button>
            </div>
          </div>
        </>
      )}

      {/* ALL JOBS TAB */}
      {activeTab === 'all jobs' && (
        jobsLoading ? (
          <div style={{ height:'160px', display:'flex', alignItems:'center', justifyContent:'center', color:T.mt }}>
            Loading…
          </div>
        ) : (
          <div style={{ border:`1px solid ${T.bd}`, borderRadius:'12px', overflow:'hidden' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead style={{ background:T.s2 }}>
                <tr>
                  {['Firm','Client','Year','Status','Created'].map(h => (
                    <th key={h} style={thStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {jobs.map((j,i) => (
                  <tr key={j.id} style={{ borderTop:`1px solid ${T.bd}` }}>
                    <td style={{ ...tdStyle, color:T.lt }}>{j.firms?.name||'—'}</td>
                    <td style={{ ...tdStyle, fontWeight:600 }}>{j.clients?.name||'—'}</td>
                    <td style={{ ...tdStyle, color:T.lt }}>{j.financial_year}</td>
                    <td style={tdStyle}><StatusBadge status={j.status} /></td>
                    <td style={{ ...tdStyle, color:T.mt }}>{fmtDate(j.created_at)}</td>
                  </tr>
                ))}
                {jobs.length === 0 && (
                  <tr><td colSpan={5} style={{ textAlign:'center', padding:'40px', color:T.mt, fontSize:'14px' }}>
                    No jobs found
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        )
      )}

      {selectedFirm && <FirmDrawer firmId={selectedFirm}
        onClose={()=>setSelected(null)}
        onDeleted={()=>{ setSelected(null); loadFirms(); }} />}
    </div>
  );
}
